'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, Shield, Heart, Leaf, BookOpen, Sparkles } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: 'linear-gradient(170deg, #F7F4F0 0%, #EDE8E1 50%, #F0EBE4 100%)' }}>
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-5 max-w-2xl mx-auto">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl lavender-gradient flex items-center justify-center shadow-card-sm">
            <Leaf size={14} className="text-white" />
          </div>
          <span className="text-sm font-bold text-foreground tracking-tight">My Little Clingy</span>
        </div>
        <Link
          href="/onboarding-sign-up-login-screen"
          className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors"
        >
          Sign in
        </Link>
      </nav>

      {/* Hero */}
      <section className="px-6 pt-14 pb-16 max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/8 border border-primary/20 mb-8">
          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
          <span className="text-[11px] font-semibold text-primary tracking-wide uppercase">A safe space, always open</span>
        </div>

        <h1 className="text-[38px] sm:text-[50px] font-extrabold text-foreground tracking-tight leading-[1.1] mb-5">
          Someone to talk to,<br />
          <span className="text-primary">whenever you need it.</span>
        </h1>

        <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-sm">
          Emotionally intelligent. Judgment-free. Always here.
        </p>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Link
            href="/chat-screen?guest=true"
            className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl lavender-gradient text-white font-bold text-base shadow-card transition-all duration-200 active:scale-95 hover:shadow-lg"
          >
            Start talking
            <ArrowRight size={16} />
          </Link>
          <Link
            href="/onboarding-sign-up-login-screen"
            className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-border bg-card text-foreground font-semibold text-base transition-all duration-200 hover:border-primary/40 hover:bg-primary/5"
          >
            Sign in
          </Link>
        </div>
      </section>

      {/* What Clingy offers — asymmetric bento */}
      <section className="px-6 pb-16 max-w-2xl mx-auto">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-5">What you'll find here</p>
        <div className="grid grid-cols-2 gap-3">
          {/* Large card — spans full width */}
          <div className="col-span-2 p-6 rounded-3xl bg-card border border-border shadow-card flex items-start gap-5">
            <div className="w-11 h-11 rounded-2xl lavender-gradient flex items-center justify-center flex-shrink-0 shadow-card-sm">
              <Heart size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-foreground text-base mb-1.5">A space to be heard</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Whatever's on your mind — big, small, or hard to name. Clingy listens without judgment, at any hour.
              </p>
            </div>
          </div>

          {/* Two smaller cards */}
          <div className="p-5 rounded-3xl bg-card border border-border shadow-card-sm flex flex-col gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Leaf size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-foreground text-sm mb-1">Understand yourself</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">Spot patterns in mood, energy, and how you feel over time.</p>
            </div>
          </div>

          <div className="p-5 rounded-3xl bg-card border border-border shadow-card-sm flex flex-col gap-3">
            <div className="w-9 h-9 rounded-xl" style={{ background: 'rgba(196, 149, 106, 0.12)' }}>
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen size={16} className="text-accent" />
              </div>
            </div>
            <div>
              <h3 className="font-bold text-foreground text-sm mb-1">Reflect and grow</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">Journaling, check-ins, and weekly insights.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Therapist-inspired reassurance */}
      <section className="px-6 pb-16 max-w-2xl mx-auto">
        <div className="p-6 rounded-3xl border border-border shadow-card" style={{ background: 'linear-gradient(135deg, #FDFAF7 0%, #F0EBE4 100%)' }}>
          <div className="flex items-start gap-4 mb-5">
            <div className="w-10 h-10 rounded-2xl lavender-gradient flex items-center justify-center flex-shrink-0 shadow-card-sm">
              <Shield size={17} className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-foreground text-base mb-0.5">Your privacy, always protected</h2>
              <p className="text-xs text-muted-foreground">Clingy is built with your safety and dignity in mind</p>
            </div>
          </div>
          <div className="space-y-3">
            {[
              'Your conversations are private and encrypted',
              'You control what Clingy remembers — edit or delete anytime',
              'Clingy supports emotional wellbeing but does not replace licensed therapy',
              'No data is sold to third parties',
            ]?.map((point, i) => (
              <div key={`trust-${i}`} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-5 pt-4 border-t border-border">
            <Link href="/privacy-policy" className="text-xs text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="text-xs text-muted-foreground hover:text-primary transition-colors">Terms</Link>
            <Link href="/safety" className="text-xs text-muted-foreground hover:text-primary transition-colors">Safety</Link>
          </div>
        </div>
      </section>

      {/* Final CTA — warm, grounded */}
      <section className="px-6 pb-20 max-w-2xl mx-auto">
        <div className="p-8 rounded-3xl lavender-gradient text-white shadow-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-white/80" />
            <span className="text-xs font-semibold text-white/80 uppercase tracking-wide">A little clingy, always caring.</span>
          </div>
          <h2 className="text-2xl font-extrabold mb-2 tracking-tight">Ready when you are.</h2>
          <p className="text-white/75 text-sm mb-6 leading-relaxed">
            There's no right time to start. Whenever you're ready, Clingy is here.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              href="/chat-screen?guest=true"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-2xl bg-white text-[#3d5c42] font-bold text-sm transition-all duration-200 hover:bg-white/90 active:scale-95 shadow-card-sm"
            >
              Start talking
              <ArrowRight size={15} />
            </Link>
            <Link
              href="/onboarding-sign-up-login-screen"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-2xl bg-white/20 text-white font-semibold text-sm transition-all duration-200 hover:bg-white/30 active:scale-95"
            >
              Create account
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-6 border-t border-border max-w-2xl mx-auto flex items-center justify-between">
        <p className="text-xs text-muted-foreground">© 2026 My Little Clingy</p>
        <p className="text-xs text-muted-foreground">Evidence-informed emotional support</p>
      </footer>
    </div>
  );
}